import React, { useState, useMemo, useEffect } from 'react';
import { usePharmacyPickerContext } from './PharmacyPickerContext';
import SearchIcon from "../../icons/Search-White.svg";
import * as translator from '../../utils/translator';
import { MainTracker } from '../../PixelTrackers/MainTracker';
import { useCustomer } from '../../customHooks/useCustomer';
import { useCartContext } from '../../customHooks/useCart';
import { normalizeText } from '../../utils/helpers';
import { IoIosPaperPlane } from 'react-icons/io';
import { IoCheckmarkCircle } from 'react-icons/io5';
import useIsMobile from '../../customHooks/useIsMobile';

interface PharmacySearchProps {
  stockLocations: {
    [stockCode: string]: {
      name: string;
      address: string;
      location?: { lat: number; lng: number };
      opening_hours?: string;
      is_external_pharmacy?: boolean;
      external_integration_code?: string;
    };
  };
  onPharmacySelect: (stockCode: string) => void;
  onPharmacyHighlight?: (stockCode: string | null) => void;
  highlightedStockCode?: string | null;
  productsCountPerPharmacy?: { [stockCode: string]: number };
  userLocation?: { lat: number; lng: number } | null;
  externalSearchQuery?: string;
  hideProductsCount?: boolean;
}

// Calculate distance between two coordinates using Haversine formula
const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const PharmacySearch: React.FC<PharmacySearchProps> = ({
  stockLocations,
  onPharmacySelect,
  onPharmacyHighlight,
  highlightedStockCode,
  productsCountPerPharmacy = {},
  userLocation,
  externalSearchQuery,
  hideProductsCount = false,
}) => {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [localUserLocation, setLocalUserLocation] = useState<{ lat: number; lng: number } | null>(userLocation || null);
  const language = localStorage.getItem("language") || "de";
  const { customerState } = useCustomer();
  const { getCart } = useCartContext();
  const { selectedStockCode, selectedService, getProductPriceForStock: getPriceForStock } = usePharmacyPickerContext();
  const [geoEnabled, setGeoEnabled] = useState<boolean>(false);
  const isMobile = useIsMobile();

  // Sync with external search query when it changes
  useEffect(() => {
    if (externalSearchQuery !== undefined) {
      setSearchQuery(externalSearchQuery);
    }
  }, [externalSearchQuery]);

  // Get user location if not provided as prop
  useEffect(() => {
    if (navigator.geolocation) {
      setGeoEnabled(true);
      if (!userLocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            setLocalUserLocation({
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            });
          },
          () => {
            // Silently fail - distance just won't be shown
            setGeoEnabled(false);
          }
        );
      }
    } else {
      setGeoEnabled(false);
    }
  }, [userLocation]);

  const currentUserLocation = userLocation || localUserLocation;

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
  };

  // Filter pharmacies based on search query
  const filteredPharmacies = useMemo(() => {
    return Object.entries(stockLocations).filter(([stockCode, pharmacy]) => {
      if (!searchQuery || searchQuery.trim().length < 1 || stockCode === selectedStockCode) return false;
      const query = normalizeText(searchQuery);

      
      return (
        normalizeText(pharmacy.name).includes(query) ||
        normalizeText(pharmacy.address).includes(query)
      );
    });
  }, [stockLocations, searchQuery, selectedStockCode]);

  // When user has typed a search, always show filtered results so search works. When no search query and a pharmacy is highlighted (e.g. from map), show only that one. Highlight is cleared only when user clicks a different pharmacy.
  const pharmaciesToDisplay = useMemo(() => {
    if (searchQuery && searchQuery.trim() !== '') {
      return filteredPharmacies;
    }
    if (highlightedStockCode && stockLocations[highlightedStockCode]) {
      return [[highlightedStockCode, stockLocations[highlightedStockCode]] as [string, typeof stockLocations[string]]];
    }
    return filteredPharmacies;
  }, [highlightedStockCode, stockLocations, filteredPharmacies, searchQuery]);

  const handlePharmacyClick = (stockCode: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const pharmacy = stockLocations[stockCode];
    if (pharmacy) {
      const userId = customerState?.customerID || null;
      
      // Calculate distance if user location and pharmacy location are available
      let distance: number | null = null;
      if (currentUserLocation && pharmacy.location) {
        distance = calculateDistance(
          currentUserLocation.lat,
          currentUserLocation.lng,
          pharmacy.location.lat,
          pharmacy.location.lng
        );
      }
      
      // Get price from selected product's available stocks
      const productsAvailablePrice = getPriceForStock?.(stockCode);
      
      // Track pharmacy card click
      MainTracker.track("custom", "pharmacy_card_click", {
        user_id: userId,
        pharmacy_id: stockCode,
        pharmacy_name: pharmacy.name,
        distance_km: distance,
        products_available_Price: productsAvailablePrice,
      });
    }
    
    // Highlight the pharmacy (pan map) but don't select it yet
    if (onPharmacyHighlight) {
      onPharmacyHighlight(stockCode);
    }
  };

  const handleChoosePharmacy = (stockCode: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    const pharmacy = stockLocations[stockCode];
    if (pharmacy) {
      const userId = customerState?.customerID || null;
      const cart = getCart();
      const userAddressAvailable = cart?.zipcode ? 'yes' : 'no';
      
      // Calculate distance if user location and pharmacy location are available
      let distance: number | null = null;
      if (currentUserLocation && pharmacy.location) {
        distance = calculateDistance(
          currentUserLocation.lat,
          currentUserLocation.lng,
          pharmacy.location.lat,
          pharmacy.location.lng
        );
      }
      
      const productsAvailableCount = productsCountPerPharmacy[stockCode] || 0;
      
      // Track pharmacy selection (critical event - binds customer selection)
      MainTracker.track("custom", "pharmacy_selected", {
        user_id: userId,
        pharmacy_id: stockCode,
        pharmacy_name: pharmacy.name,
        distance_km: distance,
        products_available_count: productsAvailableCount,
        user_address_available: userAddressAvailable,
        geo_enabled: geoEnabled ? 'yes' : 'no'
      });
    }
    
    // Actually select the pharmacy
    onPharmacySelect(stockCode);
    if (onPharmacyHighlight) {
      onPharmacyHighlight(null); // Clear highlight
    }
    setSearchQuery("");
  };

  return (
    <div className="px-4 py-2.5" id="pharmacy-search-section">

      {/* Input container with icon inside */}
      <div className="flex items-center h-14 w-full rounded-md border border-solid border-[#EDEDED] bg-white p-3 shadow-sm">
        {/* Custom placeholder wrapper */}
        <div className="relative flex-1 h-full flex items-center">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            // hide native placeholder so it doesn't overlap
            placeholder=""
            className="w-full h-full bg-transparent border-0 outline-none text-sm text-gray-900 placeholder-transparent focus:ring-0"
          />

          {/* Custom placeholder that can wrap */}
          {!searchQuery && (
            <span className="pointer-events-none absolute inset-y-0 left-0 right-0 flex items-center text-sm text-gray-400 leading-snug">
              Geben Sie die Postleitzahl, Adresse oder den Namen der Apotheke ein
            </span>
          )}
        </div>

        <button
          type="button"
          onClick={(e) => {
            if (pharmaciesToDisplay[0]) handlePharmacyClick(pharmaciesToDisplay[0][0], e);
          }}
          className="flex-shrink-0 w-10 h-10 rounded-md bg-primary-button-gradient flex items-center justify-center hover:brightness-95 cursor-pointer 
                     focus:outline-none focus:ring-0 active:outline-none active:ring-0 border-none"
          aria-label="Search"
        >
          <img
            src={SearchIcon}
            alt="Search"
            className="w-6 h-6 object-contain"
          />
        </button>
      </div>

      {/* Search Results - show when user typed (filtered list) or when user clicked map (single pharmacy) */}
      {pharmaciesToDisplay.length > 0 && (
        <div className="mt-2 flex flex-col gap-2">
          {/* Scrollable container showing 5 pharmacies initially (each item ~90px with distance, 5 items = ~450px) */}
          <div 
            className="overflow-y-auto flex flex-col gap-2 pb-2 pr-2"
            style={{ maxHeight: isMobile ? '250px' : '450px' }}
          >
            {pharmaciesToDisplay.map(([stockCode, stock]) => {
              const isHighlighted = highlightedStockCode === stockCode;
              const productsCount = productsCountPerPharmacy[stockCode] || 0;
              
              // Calculate distance if user location and pharmacy location are available
              let distance: number | null = null;
              if (currentUserLocation && stock.location) {
                distance = calculateDistance(
                  currentUserLocation.lat,
                  currentUserLocation.lng,
                  stock.location.lat,
                  stock.location.lng
                );
              }

              // Determine which badge to show - only in prescription_only service
              const isOnlyPrescriptionService = selectedService === "prescription_only";
              const showUnknownStockBadge = isOnlyPrescriptionService && stock.is_external_pharmacy && !stock.external_integration_code;
              const showPayOnceBadge = isOnlyPrescriptionService && stock.is_external_pharmacy === false;
              
              return (
                <div
                  key={stockCode}
                  onClick={(e) => handlePharmacyClick(stockCode, e)}
                  className={`px-4 py-3 rounded-lg border border-solid transition-all duration-200 ${
                    isHighlighted 
                      ? 'bg-[#F7FAEF] border-[#0A9281]' 
                      : 'bg-white border-gray-300 hover:bg-gray-50 cursor-pointer'
                  }`}
                >
                  <div className="flex flex-col gap-1">
                    {/* pharmacy info (left) | badges (right) */}
                    <div className="flex flex-row items-start justify-between gap-3 min-w-0">
                      <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                        <span className={`font-semibold text-[#00302E] ${isMobile ? 'text-sm line-clamp-2' : 'text-sm'}`}>
                          {stock.name}
                        </span>
                        <span className={`text-[#666666] ${isMobile ? 'text-xs line-clamp-2' : 'text-xs'}`}>
                          {stock.address}
                        </span>

                        {/* distance from user */}
                        {distance !== null && (
                          <div className="text-[10px] text-[#666666] flex items-center gap-1">
                            <IoIosPaperPlane className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{distance.toFixed(1).replace('.', ',')} {translator.translate(language, 'choose_treatment', 'default/km_from_you')}</span>
                          </div>
                        )}

                        {/* opening hours */}
                        {stock.opening_hours && !isMobile && (
                          <div className="flex flex-col">
                              {stock.opening_hours.split(',').map((hour, index) => (
                                  <React.Fragment key={index}>
                                      <span className="text-[#666666] text-xs font-roboto font-extralight opacity-50">{hour.replace(/null-null/, 'Geschlossen')}</span>
                                  </React.Fragment>
                              ))}
                          </div>
                        )}
                      </div>

                      <div className="flex-shrink-0 flex flex-col items-end gap-1">
                          {/* Only show products count for pharmacies with known stock, and when not hidden */}
                          {!hideProductsCount && !showUnknownStockBadge && (
                            <>
                              <p className="text-[11px]" style={{ color: '#949494' }}>
                                {productsCount > 0 ? productsCount : 0} {translator.translate(language, 'choose_treatment', 'default/products')}
                              </p>
                              <p className="text-[11px]" style={{ color: '#949494' }}>{translator.translate(language, 'choose_treatment', 'default/available')}</p>
                            </>
                          )}

                          {/* Badges */}
                          {showUnknownStockBadge && (
                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md font-semibold ${isMobile ? 'text-[10px] flex-nowrap' : 'text-xs'}`} style={{ backgroundColor: '#FF9800', color: '#FFFFFF' }}>
                              <img src="/unKnownstockVector.png" alt={translator.translate(language, 'choose_treatment', 'unknown_stock_modal/title')} className="w-4 h-4 flex-shrink-0" style={{ filter: 'brightness(0) invert(1)' }} />
                              <span className={isMobile ? 'whitespace-nowrap truncate max-w-[80px]' : ''}>{translator.translate(language, 'choose_treatment', 'unknown_stock_modal/title')}</span>
                            </div>
                          )}
                          {showPayOnceBadge && (
                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-md font-semibold ${isMobile ? 'text-[10px] flex-nowrap' : 'text-xs'}`} style={{ backgroundColor: '#037D73', color: '#FFFFFF' }}>
                              <IoCheckmarkCircle className="w-4 h-4 flex-shrink-0" />
                              <span className={isMobile ? 'whitespace-nowrap truncate max-w-[70px]' : ''}>{translator.translate(language, 'choose_treatment', 'shipping_and_service_selection/pay_once')}</span>
                            </div>
                          )}
                      </div>
                    </div>

                    {/* choose pharmacy button — full bottom row when card is highlighted */}
                    {isHighlighted && (
                      <div className="flex justify-end mt-2">
                        <button
                          onClick={(e) => handleChoosePharmacy(stockCode, e)}
                          className="px-3 py-1.5 text-white text-xs font-medium rounded-md border-none cursor-pointer"
                          style={{ backgroundColor: '#269C6E', borderRadius: '4.8px' }}
                        >
                          {translator.translate(language, 'choose_treatment', 'default/choose_pharmacy')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>     
        </div>
      )}

      {searchQuery && searchQuery.trim().length >= 1 && filteredPharmacies.length === 0 && (
        <div className="mt-2.5 p-3 bg-white border border-[#EDEDED] rounded-lg text-center text-gray-600 text-sm">
          {translator.translate(language, 'choose_treatment', 'default/no_pharmacies_found')}
        </div>
      )}
    </div>
  );
};
